<template lang="pug">
.filter-group-accordion
  h4.filter-header( @click="collapsed = !collapsed" )
    | {{ __(displayName, 'specs') }}
    Icon.icon-size-xxs(fill="#666666" :name="!collapsed ? 'arrow-up' : 'arrow-down'")
  .filter-content(
    v-if="!collapsed"
    :class="'filter-' + filterType"
  )
    Row(v-if="useBoxValues")
      Column(
        mobile="3"
        v-for="val in facet.value"
        :key="val.id"
      )
        rogue-link.filter-link(:to="val.route")
          filters-box-values(
            :filter-type="filterType"
            :filter-option="val"
            :checked="val.active"
          )
    .filter-checkboxes(v-else)
      rogue-link.filter-link(
        v-for="(val, index) in facet.value"
        v-show="limitExpanded || index < limit"
        :key="val.id + '' + filterType"
        tabindex="-1"
        :to="val.categoryRoute ? { path: val.categoryRoute, query: cleanQuery } : val.route"
      )
        Checkbox.filter-check(
          :class="filterType"
          :name="filterType + '' + val.id"
          :key="filterType + '' + val.id"
          :id="`filter-${filterType}-${val.id}`"
          :modelValue="val.active"
        )
          .filter-label
            span(v-if="(filterType === 'rating_summary' || filterType === 'manufacturer')" v-html="val.text")
            span(v-else) {{ __(val.text, filterType.indexOf('cat') === 0 ? 'categories' : 'specs') }}
      span.font-size-xxs.link(
        @click="limitExpanded = !limitExpanded"
        v-if="facet.value.length > limit"
      )
        span(v-if="!limitExpanded")
          | {{ __('+ {count} more', { count: facet.value.length - limit }) }}
        span(v-else) - {{ __('Show less') }}
</template>
<script setup lang="ts">
import { computed, defineAsyncComponent, ref } from 'vue'
import { useRoute } from 'vue-router'
import type { IReflektionFacet } from '@/js/services/rfk/types'

const AD_TRACKING_PARAMS = new Set(['gad_source', 'gad_campaignid', 'gclid', 'fbclid', 'msclkid', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'])
// Switching category resets pagination — the new category may not have that many pages
const DROPPED_PARAMS = new Set([...AD_TRACKING_PARAMS, 'page_number'])

const filtersBoxValues = defineAsyncComponent(() => import(/* webpackChunkName: "facet-box" */'./filters-box-values.vue'))

const props = defineProps<{
  facet: IReflektionFacet,
  filterType: string
}>()


const route = useRoute()
const cleanQuery = computed(() => Object.fromEntries(Object.entries(route.query).filter(([key]) => !DROPPED_PARAMS.has(key))))

const limit = ref(5)
const collapsed = ref(false)
const limitExpanded = ref(false)

const useBoxValues = computed(() => props.facet.display_name.toLowerCase().includes('color') || ['shoe size'].includes(props.facet.display_name.toLowerCase()))

const displayName = computed(() => {
  const name = props.facet.display_name

  if (name.toLowerCase() === 'is_salable') {
    return 'Availability'
  }

  if (name.includes('Category')) {
    return name.replace(/(^[\w]+).*/g, '$1')
  }

  return name
})

</script>
<style lang="scss" scoped>
.filter-header {
  display: flex;
  justify-content: space-between;
  cursor: pointer;
  font-size: $size-s;
}

.filter-group-accordion {
  margin-top: $space-s;
  border-bottom: $border-width-thin solid $color-gray-light;
}

.filter-content {
  padding-bottom: $space-s;
}

.filter-check {
  margin-bottom: $space-xxs;
  font-size: $size-xs;

  &:deep(.box) {
    margin-right: $space-xxxs;
  }
}


a {
  text-decoration: none;
  color: unset;
}
</style>